import { useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { artists, getArtistByQrSlug, DISCIPLINE_LABEL } from "@/data/artists";
import { useCatalogVersion } from "@/data/catalogLive";
import ArtworkDetail from "@/pages/ArtworkDetail";
import { useSEO } from "@/hooks/useSEO";

/**
 * Permanent, QR-friendly route: /obra/:qrslug
 * Resolves the artwork by its qrSlug (artist-slug + title-slug) and renders
 * the SAME ArtworkDetail component used by /artistas/:slug. No duplicated UI.
 */
export default function ObraDetail() {
  useCatalogVersion();
  const { qrslug } = useParams();
  const { lang } = useLang();
  const artist = getArtistByQrSlug(qrslug);

  useSEO({
    title: artist
      ? `${artist.title} — ${artist.name} | LATIR — Arteconciencia`
      : "Obra | LATIR — Arteconciencia",
    description: artist
      ? `Ficha y audioguía de "${artist.title}" de ${artist.name} en LATIR: El placer de vivir, exposición de arte contemporáneo de Arteconciencia en el Palacio Nacional de El Salvador.`
      : undefined,
    canonical: artist ? `/obra/${artist.qrSlug || qrslug}` : "/artistas",
  });

  const { prev, next } = useMemo(() => {
    if (!artist) return { prev: null, next: null };
    const i = artists.findIndex((a) => a.slug === artist.slug);
    return {
      prev: artists[(i - 1 + artists.length) % artists.length],
      next: artists[(i + 1) % artists.length],
    };
  }, [artist]);

  if (!artist) {
    return <Navigate to="/artistas" replace />;
  }

  return (
    <ArtworkDetail
      artist={artist}
      prev={prev}
      next={next}
      discipline={DISCIPLINE_LABEL[artist.discipline]?.[lang] || ""}
      num={String(artist.n || 1).padStart(2, "0")}
      totalCount={artists.length}
    />
  );
}
